import { memo, useRef } from "react";
import L from "leaflet";
import { Marker, Popup } from "react-leaflet";
import PropTypes from "prop-types";

import mapMarkerDelayed from "assets/images/mapMarkers/YellowMarker.svg";
import mapMarkerFallback from "assets/images/mapMarkers/BlackMarker.svg";
import mapMarkerIncomplete from "assets/images/mapMarkers/OrangeMarker.svg";
import mapMarkerInTransit from "assets/images/mapMarkers/BlueMarker.svg";
import Button from "components/atoms/Button/Button";
import { ReactComponent as CrossIcon } from "assets/images/icons/Cross.svg";
import { FormattedMessage } from "react-intl";

// Reuse the same Leaflet icon instances for every marker instead of allocating
// a new L.Icon per shipment on every render (1500-2000+ allocations otherwise).
const ICON_SIZE = new L.Point(40, 47);
const STATUS_ICONS = {
  "In transit": new L.Icon({ iconUrl: mapMarkerInTransit, iconSize: ICON_SIZE }),
  Delayed: new L.Icon({ iconUrl: mapMarkerDelayed, iconSize: ICON_SIZE }),
  Incomplete: new L.Icon({ iconUrl: mapMarkerIncomplete, iconSize: ICON_SIZE }),
};
const FALLBACK_ICON = new L.Icon({ iconUrl: mapMarkerFallback, iconSize: ICON_SIZE });
const getMarkerIcon = (status) => STATUS_ICONS[status] || FALLBACK_ICON;

const STATUS_COLORS = {
  "In transit": "#298CDE",
  Delayed: "#F1B92A",
  Incomplete: "#EA7939",
};
const renderColor = (status) => STATUS_COLORS[status] || "#252525";

const renderStatus = (status) => (
  <p
    className="m-0 rounded-pill font-weight-bold text-white"
    style={{
      padding: "3.5px 8px",
      backgroundColor: renderColor(status),
    }}
  >
    {status}
  </p>
);

function ShipmentMarker({ shipment, markerPosition }) {
  // Each marker owns its own popup ref so closing one popup doesn't affect siblings.
  const popupRef = useRef(null);

  const handlePopupClose = () => {
    if (popupRef?.current?.close) popupRef.current.close();
  };

  if (!shipment.origin?.origin_coordinates || !shipment.delivery?.destination_coordinates) return null;

  const {
    origin: {
      origin_coordinates: { latitude: pickupLatitude, longitude: pickupLongitude },
      origin_city: originCity,
      origin_name: originName,
      origin_country: originCountry,
    },
    delivery: {
      destination_coordinates: { latitude: deliveryLatitude, longitude: deliveryLongitude },
      destination_city: destinationCity,
      destination_name: destinationName,
      destination_country: destinationCountry,
    },
    freight_mode: { freight_mode: freightMode },
    shipment_reference: shipmentId,
    shipment_title: shipmentTitle,
    payment_location: paymentLocation,
    status,
  } = shipment;

  if (!pickupLongitude || !pickupLatitude || !deliveryLongitude || !deliveryLatitude) return null;

  const classStatus = status.toLowerCase().replace(" ", "-");
  const markerLatLon =
    markerPosition === "origin" ? [pickupLatitude, pickupLongitude] : [deliveryLatitude, deliveryLongitude];

  return (
    <Marker
      icon={getMarkerIcon(status)}
      position={markerLatLon}
      eventHandlers={{
        click: () => {
        },
      }}
      title={`${shipmentId} ${shipmentTitle}`}
    >
      <Popup ref={popupRef} className={`leaflet-pop-${classStatus}`}>
        <div className="d-flex justify-content-between align-items-center">
          {renderStatus(status)}
          <div className="cursor-pointer" onClick={handlePopupClose} role="button" tabIndex={0}>
            <CrossIcon />
          </div>
        </div>
        <div className="d-flex align-items-center mb-0 w-100 justify-content-between mt-3">
          <Button
            variant="link"
            className="mt-0 p-0 font-b9"
            label={shipmentId}
            onClick={() => {
              window.open(`/shipment/${shipmentId}/tracking`, "_blank");
            }}
          />
          <p className="font-b3 text-secondary m-0 text-right w-50">{shipmentTitle}</p>
        </div>
        <div className="d-flex align-items-center w-100 justify-content-between mb-3">
          <p className="font-b3 m-0 text-secondary">{paymentLocation}</p>
          <p className="font-b3 m-0 text-secondary">{freightMode}</p>
        </div>
        <div>
          <p className="font-b4 m-0">
            <FormattedMessage id="general.origin" defaultMessage="Origin" />
          </p>
          <p className="font-b2 mt-0">
            {originName && `${originName}, `}
            {originCity}, {originCountry}
          </p>
          <p className="font-b4 m-0">
            <FormattedMessage id="general.destination" defaultMessage="Destination" />
          </p>
          <p className="font-b2 mb-0 mt-0">
            {destinationName && `${destinationName}, `}
            {destinationCity}, {destinationCountry}
          </p>
        </div>
      </Popup>
    </Marker>
  );
}

ShipmentMarker.propTypes = {
  shipment: PropTypes.instanceOf(Object).isRequired,
  markerPosition: PropTypes.string.isRequired,
};

const MemoShipmentMarker = memo(ShipmentMarker);

function Markers({ mapData, markerPosition }) {
  if (!mapData?.length) return null;

  return (
    <>
      {mapData.map((shipment) => (
        <MemoShipmentMarker key={shipment.shipment_reference} shipment={shipment} markerPosition={markerPosition} />
      ))}
    </>
  );
}

Markers.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  mapData: PropTypes.arrayOf(PropTypes.object),
  markerPosition: PropTypes.string.isRequired,
};
Markers.defaultProps = {
  mapData: [],
};

export default memo(Markers);
